import React, { useState, useEffect, useMemo } from 'react';
import {
  TextField,
  Dropdown,
  Toggle,
  PrimaryButton,
  DefaultButton,
  Label,
  Stack,
  MessageBar,
  MessageBarType,
  Spinner,
  SpinnerSize,
  DatePicker
} from '@fluentui/react';

import { Dismiss24Regular, Save20Regular } from '@fluentui/react-icons';
import { useTranslation } from 'react-i18next';
import { FiSettings } from 'react-icons/fi';
import DashboardFilters from './DashboardFilters';
import './AnalyticsWidgetCatalog.css';

// Les dates figées d'un widget sont stockées en "YYYY-MM-DD" (jour calendaire, sans heure ni
// fuseau) : c'est ce que l'utilisateur choisit, et ça reste stable quel que soit le fuseau du
// navigateur qui rouvre le rapport. La conversion en bornes de journée est faite au moment de
// la requête, dans calculateDateRangeFromPeriod (AnalyticsDashboard).
const toDayString = (date) => {
  if (!date) return null;
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const fromDayString = (dayString) => {
  if (!dayString || typeof dayString !== 'string') return undefined;
  const [y, m, d] = dayString.split('-').map(Number);
  if (!y || !m || !d) return undefined;
  return new Date(y, m - 1, d);
};

/**
 * AnalyticsWidgetConfigPanel - Right side panel for configuring widget instances
 * Similar to CallFlow's EditNodePanel but for Analytics widgets
 */
const AnalyticsWidgetConfigPanel = ({ 
  isOpen,
  widget,
  widgetInstance,
  tabs = [],
  currentTabId,
  tenantId,
  scope,
  onSave,
  onClose,
  onDelete
}) => {
  const { t } = useTranslation();
  const [panelWidth, setPanelWidth] = useState(600);
  const [isResizing, setIsResizing] = useState(false);
  const [config, setConfig] = useState({
    name: '',
    tabId: currentTabId || '',
    enabled: true,
    refreshInterval: 60,
    useGlobalFilters: true,
    customPeriod: 'month',
    customFilters: {},
    ...widgetInstance?.config
  });
  const [errors, setErrors] = useState({});
  const [isSaving, setIsSaving] = useState(false);

  // Initialize config when widget or widgetInstance changes
  useEffect(() => {
    console.log('🎨 AnalyticsWidgetConfigPanel - Initialize config:', {
      hasWidgetInstance: !!widgetInstance,
      widgetInstance: widgetInstance,
      config: widgetInstance?.config
    });
    
    if (widgetInstance) {
      // ⚠️ IMPORTANT: D'abord appliquer widgetInstance.config, puis overrider avec les valeurs calculées
      const newConfig = {
        name: widgetInstance.name || widget?.name || '',
        tabId: widgetInstance.tabId || currentTabId || '',
        enabled: widgetInstance.enabled !== undefined ? widgetInstance.enabled : true,
        refreshInterval: 60,
        useGlobalFilters: true,
        customPeriod: 'month',
        customFilters: {},
        // Ensuite, appliquer toute la config de l'instance (qui peut overrider les valeurs par défaut)
        ...widgetInstance.config,
        // Force certaines valeurs pour éviter undefined
        customFilters: widgetInstance.config?.customFilters || {}
      };
      
      console.log('� AnalyticsWidgetConfigPanel - useEffect triggered');
      console.log('📝 Config initialized from widgetInstance:', newConfig);
      console.log('🎯 CustomFilters being set:', newConfig.customFilters);
      setConfig(newConfig);
    } else if (widget) {
      setConfig({
        name: widget.name || '',
        tabId: currentTabId || '',
        enabled: true,
        refreshInterval: 60,
        useGlobalFilters: true,
        customPeriod: 'month',
        customFilters: {}
      });
    }
  }, [widget, widgetInstance, currentTabId]);

  // Handle resize
  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!isResizing) return;
      const newWidth = window.innerWidth - e.clientX;
      if (newWidth >= 400 && newWidth <= 900) {
        setPanelWidth(newWidth);
      }
    };

    const handleMouseUp = () => {
      setIsResizing(false);
    };

    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = 'ew-resize';
      document.body.style.userSelect = 'none';
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [isResizing]);

  // Validate configuration
  const validate = () => {
    const newErrors = {};
    
    if (!config.name || !config.name.trim()) {
      newErrors.name = t('WIDGET_NAME_REQUIRED');
    }
    
    if (!config.tabId) {
      newErrors.tabId = t('PLEASE_SELECT_TAB');
    }

    // Période figée : au moins une date, sinon le widget partirait sans borne de date du tout.
    if (config.useGlobalFilters === false
      && config.customPeriod === 'custom'
      && !config.customStartDate && !config.customEndDate) {
      newErrors.customDates = t('CUSTOM_DATE_REQUIRED');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle save
  const handleSave = async () => {
    if (!validate()) {
      return;
    }

    setIsSaving(true);
    
    try {
      // Construire l'objet config en enlevant les champs root-level
      const { name, tabId, enabled, ...widgetConfig } = config;

      const widgetData = {
        id: widgetInstance?.id || `widget-${Date.now()}`,
        widgetId: widget?.id || widgetInstance?.widgetId,
        name: name,
        tabId: tabId,
        enabled: enabled,
        config: widgetConfig  // useGlobalFilters, customPeriod, customFilters, refreshInterval, etc.
      };

      console.log('💾 AnalyticsWidgetConfigPanel - Preparing widget data:', widgetData);
      console.log('🔧 Config object to save:', widgetConfig);

      if (onSave) {
        await onSave(widgetData);
      }
    } finally {
      setIsSaving(false);
    }
  };

  // Handle delete
  const handleDelete = () => {
    if (window.confirm(t('CONFIRM_DELETE_WIDGET'))) {
      if (onDelete && widgetInstance) {
        onDelete(widgetInstance.id);
      }
    }
  };

  // Handle field change
  const handleChange = (field, value) => {
    setConfig(prev => {
      const newConfig = { ...prev, [field]: value };
      
      // 🔄 Si on réactive "Use Global Filters", supprimer les filtres personnalisés
      if (field === 'useGlobalFilters' && value === true) {
        console.log('🌍 Switching to Global Filters - Clearing custom filters');
        return {
          ...newConfig,
          customPeriod: null,
          customStartDate: null,
          customEndDate: null,
          customFilters: {}
        };
      }

      // 🔄 En quittant la période figée, on oublie les dates saisies : les garder ferait
      // réapparaître une plage obsolète si l'utilisateur revient sur 'custom' plus tard.
      if (field === 'customPeriod' && value !== 'custom') {
        return {
          ...newConfig,
          customStartDate: null,
          customEndDate: null
        };
      }

      return newConfig;
    });
    
    // Clear error for this field
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  // Handle custom filters change (flows, queues, agents)
  const handleCustomFiltersChange = (filters) => {
    console.log('📥 AnalyticsWidgetConfigPanel - Received filters from DashboardFilters:', filters);
    setConfig(prev => {
      const newConfig = {
        ...prev,
        customFilters: filters
      };
      console.log('💾 AnalyticsWidgetConfigPanel - Updating config with new filters:', newConfig);
      return newConfig;
    });
  };

  // Stabilize initialFilters reference to prevent unnecessary re-renders of DashboardFilters
  // This is CRITICAL to avoid the filters being reset when config changes
  const stableInitialFilters = useMemo(() => {
    return config.customFilters || {};
  }, [config.customFilters]);

  // Prepare tab options for dropdown
  const tabOptions = tabs.map(tab => ({
    key: tab.id,
    text: tab.name
  }));

  if (!isOpen || !widget) {
    return null;
  }

  const Icon = widget.icon || FiSettings;
  const isEditMode = !!widgetInstance;

  return (
    <div 
      className={`analytics-config-panel ${isOpen ? 'open' : ''}`}
      style={{ width: panelWidth }}
    >
      {/* Resize Handle */}
      <div
        className="analytics-config-resize-handle"
        onMouseDown={() => setIsResizing(true)}
      />

      {/* Header */}
      <div className="analytics-config-header">
        <h3 className="analytics-config-title">
          <Icon className="analytics-config-title-icon" />
          {isEditMode ? t('EDIT_WIDGET') : t('CONFIGURE_WIDGET')} - {widget.name}
        </h3>
        <div className="analytics-config-actions">
          
          <PrimaryButton 
            onClick={handleSave} 
            disabled={isSaving}
            iconProps={{ iconName: 'Save' }}
            className="analytics-save-button"
          >
            {isSaving ? (
              <>
                <Spinner size={SpinnerSize.xSmall} style={{ marginRight: 8 }} />
                {t('SAVING')}
              </>
            ) : (
              <>
                <Save20Regular style={{ marginRight: 8 }} />
                {t('SAVE')}
              </>
            )}
          </PrimaryButton>
          <DefaultButton
            onClick={onClose}
            disabled={isSaving}
            iconProps={{ iconName: 'Cancel' }}
            styles={{ root: { minWidth: 'auto', padding: '8px' } }}
          >
            <Dismiss24Regular />
          </DefaultButton>
        </div>
      </div>

      {/* Content */}
      <div className="analytics-config-content">
        {/* Error Messages */}
        {Object.keys(errors).length > 0 && (
          <MessageBar
            messageBarType={MessageBarType.error}
            isMultiline={false}
            dismissButtonAriaLabel={t('CLOSE')}
            styles={{ root: { marginBottom: 16 } }}
          >
            {t('PLEASE_FIX_ERRORS')}
          </MessageBar>
        )}

        {/* Basic Configuration */}
        <div className="analytics-config-section">
        

          <div className="analytics-config-field">
            <Label required>{t('WIDGET_NAME')}</Label>
            <TextField
              value={config.name}
              onChange={(e, value) => handleChange('name', value)}
              placeholder={t('ENTER_WIDGET_NAME')}
              errorMessage={errors.name}
            />
          </div>

          <div className="analytics-config-field">
            <Label required>{t('ASSIGN_TO_TAB')}</Label>
            <Dropdown
              options={tabOptions}
              selectedKey={config.tabId}
              onChange={(e, option) => handleChange('tabId', option.key)}
              placeholder={t('SELECT_A_TAB')}
              errorMessage={errors.tabId}
            />
            
            {/* Alternative: Show tabs as buttons */}
            <div className="analytics-tab-selector">
              {tabs.map(tab => (
                <button
                  key={tab.id}
                  className={`analytics-tab-option ${config.tabId === tab.id ? 'selected' : ''}`}
                  onClick={() => handleChange('tabId', tab.id)}
                >
                  {tab.name}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* <div className="analytics-config-section">
          <h4 className="analytics-config-section-title">
            {t('Widget Settings') || 'Widget Settings'}
          </h4>

          <div className="analytics-config-field">
            <Label>{t('Refresh Interval (seconds)') || 'Refresh Interval (seconds)'}</Label>
            <TextField
              type="number"
              value={config.refreshInterval?.toString()}
              onChange={(e, value) => handleChange('refreshInterval', parseInt(value) || 60)}
              min={10}
              max={3600}
            />
          </div>

          {/* Widget Type Specific Fields */}
          {/* {widget.type === 'metric' && (
            <>
              <div className="analytics-config-field">
                <Label>{t('Display Format') || 'Display Format'}</Label>
                <Dropdown
                  options={[
                    { key: 'number', text: 'Number' },
                    { key: 'percentage', text: 'Percentage' },
                    { key: 'duration', text: 'Duration' },
                    { key: 'currency', text: 'Currency' }
                  ]}
                  selectedKey={config.format || 'number'}
                  onChange={(e, option) => handleChange('format', option.key)}
                />
              </div>
            </>
          )} */}

          {/* Chart widgets */}
          {/* {['bar', 'line', 'pie', 'donut'].includes(widget.type) && (
            <>
              <div className="analytics-config-field">
                <Toggle
                  label={t('Show Legend') || 'Show Legend'}
                  checked={config.showLegend !== false}
                  onChange={(e, checked) => handleChange('showLegend', checked)}
                />
              </div>
              <div className="analytics-config-field">
                <Toggle
                  label={t('Show Grid') || 'Show Grid'}
                  checked={config.showGrid !== false}
                  onChange={(e, checked) => handleChange('showGrid', checked)}
                />
              </div>
            </>
          )} */}
        {/* </div> */}

        {/* Widget Filters Configuration */}
        <div className="analytics-config-section">
          <h4 className="analytics-config-section-title">
            {t('WIDGET_FILTERS')}
          </h4>

          <div className="analytics-config-field">
            <Toggle
              label={t('USE_GLOBAL_DASHBOARD_FILTERS')}
              checked={config.useGlobalFilters !== false}
              onChange={(e, checked) => handleChange('useGlobalFilters', checked)}
              inlineLabel
              styles={{
                root: { marginBottom: 16 },
                pill: { 
                  backgroundColor: config.useGlobalFilters ? '#5b5fc7' : undefined,
                  ':hover': {
                    backgroundColor: config.useGlobalFilters ? '#4a4fb0' : undefined
                  }
                },
                thumb: {
                  backgroundColor: config.useGlobalFilters ? '#ffffff' : undefined
                }
              }}
            />
            <MessageBar 
              messageBarType={MessageBarType.info} 
              isMultiline={false}
              styles={{ root: { marginTop: 8, marginBottom: 16 } }}
            >
              {config.useGlobalFilters 
                ? t('WIDGET_USE_GLOBAL_FILTERS_INFO')
                : t('WIDGET_USE_CUSTOM_FILTERS_INFO')
              }
            </MessageBar>
          </div>

          {/* Custom Period Selection - Visible when NOT using global filters */}
          {!config.useGlobalFilters && (
            <div className="analytics-config-field">
              <Label>{t('CUSTOM_PERIOD')}</Label>
              <Dropdown
                options={[
                  { key: '', text: t('NO_PERIOD_FILTER') },
                  { key: 'today', text: t('TODAY') },
                  { key: 'yesterday', text: t('YESTERDAY') },
                  { key: 'lastBusinessDay', text: t('LAST_BUSINESS_DAY') },
                  { key: 'week', text: t('LAST_7_DAYS') },
                  { key: 'month', text: t('LAST_30_DAYS') },
                  { key: 'quarter', text: t('LAST_90_DAYS') },
                  { key: 'year', text: t('LAST_365_DAYS') },
                  { key: 'custom', text: t('CUSTOM_DATE_OR_RANGE') }
                ]}
                selectedKey={config.customPeriod || ''}
                onChange={(e, option) => handleChange('customPeriod', option.key)}
                placeholder={t('SELECT_PERIOD')}
                styles={{
                  dropdown: {
                    width: '100%'
                  }
                }}
              />

              {/* Date fixe ou plage figée. Une seule date renseignée = journée unique :
                  c'est le cas « une date donnée » (ex. le 01/01/2026). */}
              {config.customPeriod === 'custom' && (
                <Stack horizontal tokens={{ childrenGap: 12 }} styles={{ root: { marginTop: 12 } }}>
                  <Stack.Item grow>
                    <Label>{t('START_DATE')}</Label>
                    <DatePicker
                      value={fromDayString(config.customStartDate)}
                      onSelectDate={(date) => handleChange('customStartDate', toDayString(date))}
                      placeholder={t('SELECT_DATE')}
                      ariaLabel={t('START_DATE')}
                      formatDate={(date) => (date ? date.toLocaleDateString() : '')}
                      maxDate={new Date()}
                      allowTextInput
                      styles={{ root: { width: '100%' } }}
                    />
                  </Stack.Item>
                  <Stack.Item grow>
                    <Label>{t('END_DATE')}</Label>
                    <DatePicker
                      value={fromDayString(config.customEndDate)}
                      onSelectDate={(date) => handleChange('customEndDate', toDayString(date))}
                      placeholder={t('SELECT_DATE')}
                      ariaLabel={t('END_DATE')}
                      formatDate={(date) => (date ? date.toLocaleDateString() : '')}
                      minDate={fromDayString(config.customStartDate)}
                      maxDate={new Date()}
                      allowTextInput
                      styles={{ root: { width: '100%' } }}
                    />
                  </Stack.Item>
                </Stack>
              )}

              {config.customPeriod === 'custom' && (
                <MessageBar
                  messageBarType={errors.customDates ? MessageBarType.error : MessageBarType.info}
                  isMultiline={true}
                  styles={{ root: { marginTop: 8 } }}
                >
                  {errors.customDates || t('CUSTOM_DATE_RANGE_INFO')}
                </MessageBar>
              )}

            </div>
          )}

          {/* Dynamic Filters (Flows, Queues, Agents) - Visible when NOT using global filters */}
          {!config.useGlobalFilters && (
            <div className="analytics-config-field" style={{ marginTop: 5 }}>
              <Label>{t('WIDGET_SPECIFIC_FILTERS')}</Label>
             
              
              {tenantId && scope && (
                <DashboardFilters
                  tenantId={tenantId}
                  scope={scope}
                  onFilterChange={handleCustomFiltersChange}
                  initialFilters={stableInitialFilters}
                />
              )}
            </div>
          )}
        </div>

        {/* Data Source Configuration 
        {widget.dataSource && (
          <div className="analytics-config-section">
            <h4 className="analytics-config-section-title">
              {t('DATA_SOURCE')}
            </h4>
            <MessageBar messageBarType={MessageBarType.info}>
              {t('QUERY_ID')}: {widget.dataSource.queryId}
            </MessageBar>
          </div>
        )}*/}
      </div>
    </div>
  );
};

export default AnalyticsWidgetConfigPanel;
